import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon, TrashIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { DatePicker } from '../../../components/ui/DatePicker';
import { Badge } from '../../../components/ui/Badge';
interface PaymentVoucher {
  id: string;
  voucherNo: string;
  date: string;
  transactionMode: 'Cash' | 'Bank';
  bankName?: string;
  accountNo?: string;
  partyName?: string;
  amount: number;
  status: 'Draft' | 'Pending' | 'Approved' | 'Rejected';
  createdBy: string;
  createdAt: string;
}
export function PaymentVoucherList() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<PaymentVoucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    transactionMode: '',
    status: '',
    search: ''
  });
  const userRole = 'Finance Manager';
  useEffect(() => {
    loadVouchers();
  }, [filters]);
  const loadVouchers = async () => {
    setLoading(true);
    try {
      const mockData: PaymentVoucher[] = [{
        id: '1',
        voucherNo: 'PV-2024-001',
        date: '2024-01-15',
        transactionMode: 'Bank',
        bankName: 'Premier Bank',
        accountNo: '1234567890',
        partyName: 'ABC Suppliers Ltd',
        amount: 30000,
        status: 'Pending',
        createdBy: 'John Doe',
        createdAt: '2024-01-15 10:30 AM'
      }, {
        id: '2',
        voucherNo: 'PV-2024-002',
        date: '2024-01-16',
        transactionMode: 'Cash',
        partyName: 'XYZ Services Inc',
        amount: 15000,
        status: 'Approved',
        createdBy: 'Jane Smith',
        createdAt: '2024-01-16 02:15 PM'
      }, {
        id: '3',
        voucherNo: 'PV-2024-003',
        date: '2024-01-17',
        transactionMode: 'Bank',
        bankName: 'Dahabshiil Bank',
        accountNo: '9876543210',
        partyName: 'DEF Trading Co',
        amount: 45000,
        status: 'Draft',
        createdBy: 'Ahmed Ali',
        createdAt: '2024-01-17 11:00 AM'
      }];
      setVouchers(mockData);
    } catch (error) {
      console.error('Failed to load vouchers:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this voucher?')) {
      try {
        console.log('Deleting voucher:', id);
        loadVouchers();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };
  const handleApprove = async (id: string) => {
    try {
      console.log('Approving voucher:', id);
      loadVouchers();
    } catch (error: any) {
      alert(error.message);
    }
  };
  const canEdit = (status: string) => {
    return status === 'Draft' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canDelete = (status: string) => {
    return status === 'Draft' && (userRole === 'Accountant' || userRole === 'Finance Manager');
  };
  const canApprove = () => {
    return userRole === 'Finance Manager';
  };
  const transactionModeOptions = [{
    value: '',
    label: 'All Modes'
  }, {
    value: 'Cash',
    label: 'Cash'
  }, {
    value: 'Bank',
    label: 'Bank'
  }];
  const statusOptions = [{
    value: '',
    label: 'All Status'
  }, {
    value: 'Draft',
    label: 'Draft'
  }, {
    value: 'Pending',
    label: 'Pending'
  }, {
    value: 'Approved',
    label: 'Approved'
  }, {
    value: 'Rejected',
    label: 'Rejected'
  }];
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    const map: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      Draft: 'default',
      Pending: 'warning',
      Approved: 'success',
      Rejected: 'danger'
    };
    return map[status] || 'default';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1860">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-1861">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1862">
          <div data-id="element-1863">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1864">
              Payment Voucher List
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1865">
              Manage cash and bank payments
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1866">
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-1867">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-1868" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            {userRole !== 'Auditor' && <Button onClick={() => navigate('/voucher/payment/create')} data-id="element-1869">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-1870" />
                Create Payment Voucher
              </Button>}
          </div>
        </div>

        {/* Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-1871">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-1872">
              Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1873">
              <Input label="Search" placeholder="Voucher No, Party Name..." value={filters.search} onChange={e => setFilters({
            ...filters,
            search: e.target.value
          })} data-id="element-1874" />

              <Select label="Transaction Mode" options={transactionModeOptions} value={filters.transactionMode} onChange={e => setFilters({
            ...filters,
            transactionMode: e.target.value
          })} data-id="element-1875" />

              <Select label="Status" options={statusOptions} value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value
          })} data-id="element-1876" />

              <DatePicker label="Date From" value={filters.dateFrom} onChange={date => setFilters({
            ...filters,
            dateFrom: date
          })} data-id="element-1877" />

              <DatePicker label="Date To" value={filters.dateTo} onChange={date => setFilters({
            ...filters,
            dateTo: date
          })} data-id="element-1878" />
            </div>
            <div className="flex justify-end" data-id="element-1879">
              <Button variant="secondary" size="sm" onClick={() => setFilters({
            dateFrom: '',
            dateTo: '',
            transactionMode: '',
            status: '',
            search: ''
          })} data-id="element-1880">
                Clear Filters
              </Button>
            </div>
          </div>}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-1881">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1882">
              Loading vouchers...
            </div> : <div className="overflow-x-auto" data-id="element-1883">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1884">
                <thead className="bg-gray-50" data-id="element-1885">
                  <tr data-id="element-1886">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1887">
                      Voucher No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1888">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1889">
                      Transaction Mode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1890">
                      Bank Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1891">
                      Account No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1892">
                      Party Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1893">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1894">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1895">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1896">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1897">
                  {vouchers.length === 0 ? <tr data-id="element-1898">
                      <td colSpan={10} className="px-6 py-12 text-center text-gray-500" data-id="element-1899">
                        No vouchers found. Click "Create Payment Voucher" to add
                        one.
                      </td>
                    </tr> : vouchers.map(voucher => <tr key={voucher.id} className="hover:bg-gray-50" data-id="element-1900">
                        <td className="px-6 py-4" data-id="element-1901">
                          <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1902">
                            {voucher.voucherNo}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1903">
                          {new Date(voucher.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1904">
                          {voucher.transactionMode}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1905">
                          {voucher.bankName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-1906">
                          {voucher.accountNo || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1907">
                          {voucher.partyName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-1908">
                          ${voucher.amount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-1909">
                          <Badge variant={getStatusVariant(voucher.status)} data-id="element-1910">
                            {voucher.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1911">
                          {voucher.createdBy}
                        </td>
                        <td className="px-6 py-4" data-id="element-1912">
                          <div className="flex items-center justify-center gap-2" data-id="element-1913">
                            <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-gray-600 hover:text-gray-900" title="View" data-id="element-1914">
                              <EyeIcon className="w-4 h-4" data-id="element-1915" />
                            </button>
                            {canEdit(voucher.status) && <button onClick={() => navigate(`/voucher/${voucher.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-1916">
                                <EditIcon className="w-4 h-4" data-id="element-1917" />
                              </button>}
                            {canDelete(voucher.status) && <button onClick={() => handleDelete(voucher.id)} className="text-red-600 hover:text-red-800" title="Delete" data-id="element-1918">
                                <TrashIcon className="w-4 h-4" data-id="element-1919" />
                              </button>}
                            {canApprove() && voucher.status === 'Pending' && <button onClick={() => handleApprove(voucher.id)} className="text-green-600 hover:text-green-800" title="Approve" data-id="element-1920">
                                <CheckIcon className="w-4 h-4" data-id="element-1921" />
                              </button>}
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}